import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatVND } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Search, Plus, Pencil, Trash2, Package, Star, X, ChevronDown, ChevronRight,
  ChevronLeft, ArrowUpDown, ArrowUp, ArrowDown, FileDown, FileUp, Loader2,
  Eye, MoreHorizontal, Power, Barcode, Info, Warehouse, ListTree, Layers, RefreshCw,
} from "lucide-react";
import { GroupNode, ProductDetail } from "@/components/ProductComponents";

function FilterSection({ title, open, toggle, children }) {
  return (
    <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-3">
      <button onClick={toggle} className="flex items-center justify-between w-full text-left">
        <span className="text-[10px] text-slate-400 uppercase font-bold">{title}</span>
        <ChevronDown className={`w-3 h-3 text-slate-500 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="mt-2 space-y-1.5">{children}</div>}
    </div>
  );
}

const MENU_TYPE_OPTIONS = ["Đồ ăn", "Đồ uống", "Khác"];
const PRODUCT_TYPE_OPTIONS = ["Hàng hóa thường", "Hàng hóa - sản xuất", "Chế biến", "Dịch vụ", "Combo - Đóng gói", "Combo tùy chọn", "Buffet gọi món"];
const STOCK_FILTER_OPTIONS = [
  { value: "", label: "Tất cả" },
  { value: "below_min", label: "Dưới định mức tồn" },
  { value: "above_min", label: "Vượt định mức tồn" },
  { value: "in_stock", label: "Còn hàng trong kho" },
  { value: "out_stock", label: "Hết hàng trong kho" },
];
const PAGE_SIZES = [10, 20, 50, 100];

export default function ProductsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filterMenuType, setFilterMenuType] = useState("");
  const [filterProductType, setFilterProductType] = useState("");
  const [filterGroup, setFilterGroup] = useState("");
  const [filterStock, setFilterStock] = useState("");
  const [filterActive, setFilterActive] = useState("active");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterTax, setFilterTax] = useState("");
  const [filterPoints, setFilterPoints] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [expandedRow, setExpandedRow] = useState(null);
  const [expandedTab, setExpandedTab] = useState("info");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [delConfirm, setDelConfirm] = useState(null);
  const [syncing, setSyncing] = useState(false);
  // Sidebar collapse
  const [sidebarSections, setSidebarSections] = useState({ menu: true, type: true, group: true, stock: true, display: true, tax: true, points: true, location: true });
  const [groupSearch, setGroupSearch] = useState("");

  const doSearch = () => { setSearch(searchInput); setPage(1); };

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    p.set("page", page); p.set("limit", pageSize);
    if (search) p.set("search", search);
    if (filterMenuType) p.set("menu_type", filterMenuType);
    if (filterProductType) p.set("product_type", filterProductType);
    if (filterGroup) p.set("group", filterGroup);
    if (filterStock) p.set("stock_filter", filterStock);
    if (filterActive === "active") p.set("active", "true");
    else if (filterActive === "inactive") p.set("active", "false");
    if (filterLocation) p.set("location", filterLocation);
    if (filterTax) p.set("tax", filterTax);
    if (filterPoints) p.set("earn_points", filterPoints);
    p.set("sort_by", sortBy); p.set("sort_dir", sortDir);
    return p.toString();
  }, [page, pageSize, search, filterMenuType, filterProductType, filterGroup, filterStock, filterActive, filterLocation, filterTax, filterPoints, sortBy, sortDir]);

  const { data, isLoading } = useQuery({
    queryKey: ["products-kv", queryParams],
    queryFn: () => api.get(`/products?source=coffee&${queryParams}`).then(r => r.data),
    keepPreviousData: true,
  });
  const products = data?.products || [];
  const totalProducts = data?.total || 0;
  const totalPages = data?.pages || 1;

  const { data: groupsData } = useQuery({
    queryKey: ["product-groups"],
    queryFn: () => api.get("/products/groups").then(r => r.data.groups || []),
  });
  const groups = groupsData || [];

  const { data: sidebarMeta } = useQuery({
    queryKey: ["product-sidebar-meta"],
    queryFn: () => api.get("/products/sidebar-meta").then(r => r.data),
  });

  const handleSyncGroups = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/products/sync-groups");
      toast.success(data.message);
      qc.invalidateQueries({ queryKey: ["product-groups"] });
      qc.invalidateQueries({ queryKey: ["product-sidebar-meta"] });
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi đồng bộ"); }
    finally { setSyncing(false); }
  };

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortDir("asc"); }
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 text-slate-600" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3 text-lime-400" /> : <ArrowDown className="w-3 h-3 text-lime-400" />;
  };

  const handleDelete = async () => {
    if (!delConfirm) return;
    try {
      await api.delete(`/products/${delConfirm.id}`);
      toast.success("Đã xoá"); qc.invalidateQueries({ queryKey: ["products-kv"] });
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
    finally { setDelConfirm(null); }
  };

  const toggleSection = (key) => setSidebarSections(s => ({ ...s, [key]: !s[key] }));

  const fmtPrice = (v) => v ? formatVND(v) : "—";

  return (
    <div className="flex gap-4 h-[calc(100vh-80px)]" data-testid="products-page">
      {/* === SIDEBAR FILTERS === */}
      <aside className="w-56 shrink-0 overflow-y-auto space-y-3 pr-2 scrollbar-thin" data-testid="products-sidebar">
        {/* Search */}
        <div className="bg-[#1e293b] border border-[#334155] rounded-lg p-3">
          <Label className="text-[10px] text-slate-400 uppercase font-bold mb-1.5 block">Tìm kiếm</Label>
          <div className="flex gap-1">
            <Input value={searchInput} onChange={e => setSearchInput(e.target.value)} onKeyDown={e => e.key === "Enter" && doSearch()} placeholder="Mã, tên hàng" className="bg-[#0f172a] border-[#334155] text-slate-200 h-7 text-[10px]" />
            <Button onClick={doSearch} size="sm" className="h-7 w-7 p-0 bg-indigo-500"><Search className="w-3 h-3" /></Button>
          </div>
        </div>

        {/* Loại thực đơn */}
        <FilterSection title="Loại thực đơn" open={sidebarSections.menu} toggle={() => toggleSection("menu")}>
          {MENU_TYPE_OPTIONS.map(t => (
            <label key={t} className="flex items-center gap-2 text-[10px] text-slate-300 cursor-pointer hover:text-slate-100">
              <input type="checkbox" checked={filterMenuType === t} onChange={() => { setFilterMenuType(filterMenuType === t ? "" : t); setPage(1); }} className="rounded w-3 h-3 bg-[#0f172a] border-[#334155]" />
              {t}
            </label>
          ))}
        </FilterSection>

        {/* Loại hàng */}
        <FilterSection title="Loại hàng" open={sidebarSections.type} toggle={() => toggleSection("type")}>
          {PRODUCT_TYPE_OPTIONS.map(t => (
            <label key={t} className="flex items-center gap-2 text-[10px] text-slate-300 cursor-pointer hover:text-slate-100">
              <input type="checkbox" checked={filterProductType === t} onChange={() => { setFilterProductType(filterProductType === t ? "" : t); setPage(1); }} className="rounded w-3 h-3 bg-[#0f172a] border-[#334155]" />
              {t}
            </label>
          ))}
        </FilterSection>

        {/* Nhóm hàng */}
        <FilterSection title="Nhóm hàng" open={sidebarSections.group} toggle={() => toggleSection("group")}>
          <Input value={groupSearch} onChange={e => setGroupSearch(e.target.value)} placeholder="Tìm nhóm hàng" className="bg-[#0f172a] border-[#334155] text-slate-200 h-6 text-[9px] mb-1.5" />
          <div className="space-y-0.5 max-h-[200px] overflow-y-auto">
            <button onClick={() => { setFilterGroup(""); setPage(1); }} className={`text-[10px] w-full text-left px-1 py-0.5 rounded ${!filterGroup ? "text-indigo-400 font-bold" : "text-slate-400 hover:text-slate-200"}`}>Tất cả</button>
            {groups.filter(g => !groupSearch || g.name.toLowerCase().includes(groupSearch.toLowerCase())).map(g => (
              <GroupNode key={g.path} group={g} selected={filterGroup} onSelect={(p) => { setFilterGroup(p); setPage(1); }} depth={0} />
            ))}
          </div>
        </FilterSection>

        {/* Tồn kho */}
        <FilterSection title="Tồn kho" open={sidebarSections.stock} toggle={() => toggleSection("stock")}>
          {STOCK_FILTER_OPTIONS.map(o => (
            <label key={o.value} className="flex items-center gap-2 text-[10px] text-slate-300 cursor-pointer">
              <input type="radio" name="stock" checked={filterStock === o.value} onChange={() => { setFilterStock(o.value); setPage(1); }} className="w-3 h-3" />
              {o.label}
            </label>
          ))}
        </FilterSection>

        {/* Thuế */}
        <FilterSection title="Thuế" open={sidebarSections.tax} toggle={() => toggleSection("tax")}>
          <label className="flex items-center gap-2 text-[10px] text-slate-300 cursor-pointer">
            <input type="radio" name="tax" checked={!filterTax} onChange={() => { setFilterTax(""); setPage(1); }} className="w-3 h-3" />
            Tất cả
          </label>
          {(sidebarMeta?.taxes || []).map(t => (
            <label key={t} className="flex items-center gap-2 text-[10px] text-slate-300 cursor-pointer">
              <input type="radio" name="tax" checked={filterTax === t} onChange={() => { setFilterTax(t); setPage(1); }} className="w-3 h-3" />
              {t}
            </label>
          ))}
        </FilterSection>

        {/* Tích điểm */}
        <FilterSection title="Tích điểm" open={sidebarSections.points} toggle={() => toggleSection("points")}>
          <label className="flex items-center gap-2 text-[10px] text-slate-300 cursor-pointer">
            <input type="radio" name="points" checked={!filterPoints} onChange={() => { setFilterPoints(""); setPage(1); }} className="w-3 h-3" />
            Tất cả
          </label>
          <label className="flex items-center gap-2 text-[10px] text-slate-300 cursor-pointer">
            <input type="radio" name="points" checked={filterPoints === "1"} onChange={() => { setFilterPoints("1"); setPage(1); }} className="w-3 h-3" />
            Tích điểm ({sidebarMeta?.points_yes || 0})
          </label>
          <label className="flex items-center gap-2 text-[10px] text-slate-300 cursor-pointer">
            <input type="radio" name="points" checked={filterPoints === "0"} onChange={() => { setFilterPoints("0"); setPage(1); }} className="w-3 h-3" />
            Không tích điểm ({sidebarMeta?.points_no || 0})
          </label>
        </FilterSection>

        {/* Vị trí */}
        <FilterSection title="Vị trí" open={sidebarSections.location} toggle={() => toggleSection("location")}>
          <select value={filterLocation} onChange={e => { setFilterLocation(e.target.value); setPage(1); }}
            className="w-full bg-[#0f172a] border border-[#334155] rounded-md px-2 py-1 text-[10px] text-slate-300" data-testid="filter-location">
            <option value="">Tất cả vị trí</option>
            {(sidebarMeta?.locations || []).map(l => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </FilterSection>

        {/* Lựa chọn hiển thị */}
        <FilterSection title="Lựa chọn hiển thị" open={sidebarSections.display} toggle={() => toggleSection("display")}>
          <label className="flex items-center gap-2 text-[10px] text-slate-300 cursor-pointer">
            <input type="radio" name="display" checked={filterActive === "active"} onChange={() => { setFilterActive("active"); setPage(1); }} className="w-3 h-3" />
            Hàng đang kinh doanh
          </label>
          <label className="flex items-center gap-2 text-[10px] text-slate-300 cursor-pointer">
            <input type="radio" name="display" checked={filterActive === "inactive"} onChange={() => { setFilterActive("inactive"); setPage(1); }} className="w-3 h-3" />
            Hàng ngừng kinh doanh
          </label>
          <label className="flex items-center gap-2 text-[10px] text-slate-300 cursor-pointer">
            <input type="radio" name="display" checked={filterActive === ""} onChange={() => { setFilterActive(""); setPage(1); }} className="w-3 h-3" />
            Tất cả
          </label>
        </FilterSection>

        <div className="pt-1 border-t border-[#334155]">
          <p className="text-[9px] text-slate-600">Số bản ghi: <select value={pageSize} onChange={e => { setPageSize(+e.target.value); setPage(1); }} className="bg-[#0f172a] border border-[#334155] rounded px-1 text-slate-300 text-[9px]">{PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}</select></p>
        </div>
      </aside>

      {/* === MAIN CONTENT === */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-lg font-bold text-[#F8FAFC] flex items-center gap-2"><Package className="w-5 h-5 text-indigo-400" /> Hàng hóa</h1>
          <div className="flex items-center gap-2">
            <Button onClick={handleSyncGroups} disabled={syncing} className="bg-violet-500 hover:bg-violet-400 text-white h-8 text-xs gap-1" data-testid="sync-groups-btn">
              {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Đồng bộ liên kết nhóm
            </Button>
            <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-emerald-500 hover:bg-emerald-400 text-white h-8 text-xs gap-1" data-testid="add-product-btn"><Plus className="w-3.5 h-3.5" /> Thêm mới</Button>
            <label className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-[#0f172a] rounded-md cursor-pointer text-xs font-semibold h-8">
              <FileUp className="w-3.5 h-3.5" /> Import
              <input type="file" accept=".xlsx,.xls" onChange={async (e) => {
                const f = e.target.files?.[0]; if (!f) return;
                const fd = new FormData(); fd.append("file", f);
                try { const { data: d } = await api.post("/products/import-excel", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(d.message); qc.invalidateQueries({ queryKey: ["products-kv"] }); } catch (err) { toast.error(err.response?.data?.detail || "Lỗi"); }
                e.target.value = "";
              }} className="hidden" />
            </label>
            <Button variant="outline" size="sm" className="border-[#334155] text-slate-300 h-8 text-xs gap-1" onClick={async () => {
              try { const { data: d } = await api.get("/products/export"); toast.success(`Xuất ${d.total} sản phẩm`); } catch { toast.error("Lỗi"); }
            }}><FileDown className="w-3.5 h-3.5" /> Xuất file</Button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto bg-[#1e293b] border border-[#334155] rounded-xl">
          <table className="w-full text-[10px] min-w-[900px]">
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#0f172a] text-slate-400 uppercase border-b border-[#334155]">
                <th className="py-2 px-2 w-7"></th>
                <th className="py-2 px-2 w-6"><Star className="w-3 h-3 text-slate-600" /></th>
                <th className="py-2 px-2 text-left cursor-pointer" onClick={() => handleSort("code")}><span className="flex items-center gap-1">Mã hàng hóa <SortIcon field="code" /></span></th>
                <th className="py-2 px-2 text-left cursor-pointer" onClick={() => handleSort("name")}><span className="flex items-center gap-1">Tên hàng <SortIcon field="name" /></span></th>
                <th className="py-2 px-2 text-left">Loại thực đơn</th>
                <th className="py-2 px-2 text-right cursor-pointer" onClick={() => handleSort("price")}><span className="flex items-center gap-1 justify-end">Giá trước thuế <SortIcon field="price" /></span></th>
                <th className="py-2 px-2 text-center">Thuế (%)</th>
                <th className="py-2 px-2 text-right">Giá sau thuế</th>
                <th className="py-2 px-2 text-right">Giá vốn</th>
                <th className="py-2 px-2 text-right cursor-pointer" onClick={() => handleSort("stock")}><span className="flex items-center gap-1 justify-end">Tồn kho <SortIcon field="stock" /></span></th>
                <th className="py-2 px-2 text-center">Đặt hàng</th>
                <th className="py-2 px-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={12} className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin text-indigo-400 mx-auto" /></td></tr>
              ) : products.length === 0 ? (
                <tr><td colSpan={12} className="py-12 text-center text-slate-500">Không có sản phẩm phù hợp</td></tr>
              ) : products.map(p => (
                <React.Fragment key={p.id}>
                  <tr className={`border-t border-[#334155]/30 hover:bg-[#0f172a]/40 cursor-pointer ${expandedRow === p.id ? "bg-indigo-500/5 border-indigo-500/20" : ""}`} onClick={() => setExpandedRow(expandedRow === p.id ? null : p.id)} data-testid={`product-row-${p.id}`}>
                    <td className="py-2 px-2 text-center"><ChevronRight className={`w-3 h-3 text-slate-500 transition-transform ${expandedRow === p.id ? "rotate-90" : ""}`} /></td>
                    <td className="py-2 px-2"><Star className="w-3 h-3 text-slate-600 hover:text-amber-400 cursor-pointer" /></td>
                    <td className="py-2 px-2 text-indigo-400 font-mono font-medium">{p.kv_code || p.id?.slice(-6)}</td>
                    <td className="py-2 px-2 text-slate-200 font-medium">{p.name}</td>
                    <td className="py-2 px-2 text-slate-400">{p.product_type || p.category_name || "—"}</td>
                    <td className="py-2 px-2 text-right text-slate-300">{fmtPrice(p.price)}</td>
                    <td className="py-2 px-2 text-center text-slate-400">{p.tax || "8%"}</td>
                    <td className="py-2 px-2 text-right text-slate-200 font-medium">{fmtPrice(p.price ? Math.round(p.price * 1.08) : 0)}</td>
                    <td className="py-2 px-2 text-right text-slate-400">{p.cost > 0 ? fmtPrice(p.cost) : "0"}</td>
                    <td className={`py-2 px-2 text-right font-medium ${p.stock_qty < 0 ? "text-red-400" : p.stock_qty === 0 ? "text-slate-500" : "text-emerald-400"}`}>{p.stock_qty || 0}</td>
                    <td className="py-2 px-2 text-center text-slate-500">0</td>
                    <td className="py-2 px-2 text-center" onClick={e => e.stopPropagation()}>
                      <button className="text-slate-500 hover:text-slate-300"><MoreHorizontal className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                  {/* Expanded Detail */}
                  {expandedRow === p.id && (
                    <tr className="bg-[#0f172a]/60">
                      <td colSpan={12} className="p-0">
                        <ProductDetail product={p} tab={expandedTab} setTab={setExpandedTab} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-2 px-1">
          <p className="text-[10px] text-slate-500">Hiển thị {(page-1)*pageSize+1} - {Math.min(page*pageSize, totalProducts)} trên tổng số <span className="text-slate-300 font-bold">{totalProducts}</span> hàng hóa</p>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(1)} className="h-6 w-6 p-0 text-slate-400">«</Button>
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-6 w-6 p-0 text-slate-400"><ChevronLeft className="w-3 h-3" /></Button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pn = page <= 3 ? i + 1 : page + i - 2;
              if (pn > totalPages) return null; if (pn < 1) return null;
              return <Button key={pn} variant={pn === page ? "default" : "ghost"} size="sm" onClick={() => setPage(pn)} className={`h-6 w-6 p-0 text-[10px] ${pn === page ? "bg-indigo-500 text-white" : "text-slate-400"}`}>{pn}</Button>;
            })}
            {totalPages > 5 && <span className="text-slate-600 text-[10px]">...</span>}
            <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-6 w-6 p-0 text-slate-400"><ChevronRight className="w-3 h-3" /></Button>
            <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)} className="h-6 w-6 p-0 text-slate-400">»</Button>
          </div>
        </div>
      </main>

      {/* Delete Confirm */}
      <Dialog open={!!delConfirm} onOpenChange={() => setDelConfirm(null)}>
        <DialogContent className="bg-[#1e293b] border-[#334155] max-w-sm">
          <DialogHeader><DialogTitle className="text-red-400">Xác nhận xoá</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-300">Bạn chắc chắn muốn xoá <span className="font-bold">{delConfirm?.name}</span>?</p>
          <DialogFooter><Button variant="ghost" onClick={() => setDelConfirm(null)} className="text-slate-400">Hủy</Button><Button onClick={handleDelete} className="bg-red-500 text-white">Xoá</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Product Modal */}
      <AddProductModal open={modalOpen} onClose={() => setModalOpen(false)} editing={editing} groups={groups} onSaved={() => { qc.invalidateQueries({ queryKey: ["products-kv"] }); setModalOpen(false); }} />
    </div>
  );
}

// ==================== ADD/EDIT PRODUCT MODAL ====================
function AddProductModal({ open, onClose, editing, groups, onSaved }) {
  const [tab, setTab] = useState("info");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: "", name: "", menu_type: "Đồ uống", product_type: "Hàng hóa thường",
    category: "", sell_price: 0, cost_price: 0, tax: "8%", unit: "",
    can_sell_direct: true, earn_points: true, is_addon: false,
    manage_stock: false, stock: 0, min_stock: 0, max_stock: 999999999,
    attributes: [], // [{name: "SIZE", values: ["S","M","L"]}]
    sync_kiotviet: false,
  });
  const [attrName, setAttrName] = useState("");
  const [attrValue, setAttrValue] = useState("");

  useEffect(() => {
    if (editing) {
      setForm({
        code: editing.kv_code || "", name: editing.name || "", menu_type: editing.product_type || "Đồ uống",
        product_type: "Hàng hóa thường", category: editing.full_category || editing.category_name || "",
        sell_price: editing.price || 0, cost_price: editing.cost || 0, tax: editing.tax || "8%",
        unit: editing.unit || "", can_sell_direct: true, earn_points: true, is_addon: false,
        manage_stock: editing.stock_tracking || false, stock: editing.stock_qty || 0,
        min_stock: editing.min_stock || 0, max_stock: 999999999, attributes: [], sync_kiotviet: false,
      });
    } else {
      setForm({ code: "", name: "", menu_type: "Đồ uống", product_type: "Hàng hóa thường", category: "", sell_price: 0, cost_price: 0, tax: "8%", unit: "", can_sell_direct: true, earn_points: true, is_addon: false, manage_stock: false, stock: 0, min_stock: 0, max_stock: 999999999, attributes: [], sync_kiotviet: false });
    }
    setTab("info");
  }, [editing, open]);

  const upd = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const taxRate = parseFloat(form.tax) / 100 || 0;
  const priceAfterTax = Math.round(form.sell_price * (1 + taxRate));

  const addAttribute = () => {
    if (!attrName) return;
    setForm(f => ({ ...f, attributes: [...f.attributes, { name: attrName, values: [] }] }));
    setAttrName("");
  };
  const addAttrValue = (idx) => {
    if (!attrValue) return;
    setForm(f => {
      const attrs = [...f.attributes];
      attrs[idx] = { ...attrs[idx], values: [...attrs[idx].values, attrValue] };
      return { ...f, attributes: attrs };
    });
    setAttrValue("");
  };
  const removeAttr = (idx) => setForm(f => ({ ...f, attributes: f.attributes.filter((_, i) => i !== idx) }));
  const removeAttrVal = (attrIdx, valIdx) => setForm(f => {
    const attrs = [...f.attributes];
    attrs[attrIdx] = { ...attrs[attrIdx], values: attrs[attrIdx].values.filter((_, i) => i !== valIdx) };
    return { ...f, attributes: attrs };
  });

  const handleSave = async (addAnother = false) => {
    if (!form.name) { toast.error("Tên hàng không được để trống"); return; }
    setSaving(true);
    try {
      const body = {
        name: form.name, code: form.code, menu_type: form.menu_type,
        product_type: form.product_type, category: form.category,
        sell_price: Number(form.sell_price) || 0, cost_price: Number(form.cost_price) || 0,
        tax: form.tax, unit: form.unit, is_active: true,
        can_sell_direct: form.can_sell_direct, is_addon: form.is_addon,
        manage_stock: form.manage_stock, stock: Number(form.stock) || 0,
        min_stock: Number(form.min_stock) || 0, max_stock: Number(form.max_stock) || 999999999,
        attributes: form.attributes, sync_kiotviet: form.sync_kiotviet,
      };
      await api.post("/products/create-full", body);
      toast.success(`Đã lưu "${form.name}"`);
      onSaved();
      if (addAnother) {
        setForm(f => ({ ...f, code: "", name: "", sell_price: 0, cost_price: 0, stock: 0, attributes: [] }));
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Lỗi lưu sản phẩm"); }
    finally { setSaving(false); }
  };

  const TABS = [
    { key: "info", label: "Thông tin" },
    { key: "detail", label: "Mô tả chi tiết" },
    { key: "ingredients", label: "Thành phần" },
    { key: "addons", label: "Món thêm" },
  ];

  // Flatten groups for select
  const flatGroups = useMemo(() => {
    const result = [];
    const walk = (nodes, prefix = "") => {
      for (const g of nodes) {
        result.push({ path: g.path, label: prefix + g.name });
        if (g.children) walk(g.children, prefix + "  ");
      }
    };
    walk(groups || []);
    return result;
  }, [groups]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#1e293b] border-[#334155] max-w-2xl max-h-[85vh] overflow-y-auto p-0" data-testid="add-product-modal">
        <DialogHeader className="px-5 pt-4 pb-0">
          <DialogTitle className="text-[#F8FAFC] text-base flex items-center gap-2">
            <Package className="w-4 h-4 text-indigo-400" />
            {editing ? "Cập nhật hàng hóa" : "Thêm hàng hóa"}
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-[#334155] px-5">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`px-3 py-2 text-xs border-b-2 transition-colors ${tab === t.key ? "border-indigo-400 text-indigo-400" : "border-transparent text-slate-500 hover:text-slate-300"}`}>{t.label}</button>
          ))}
        </div>

        <div className="px-5 py-4 space-y-4">
          {tab === "info" && (
            <>
              {/* Row 1: Mã + Tên */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-400 text-[10px]">Mã hàng hóa</Label>
                  <Input value={form.code} onChange={e => upd("code", e.target.value)} placeholder="Mã hàng tự động" className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1 h-8 text-xs" data-testid="product-code" />
                </div>
                <div>
                  <Label className="text-slate-400 text-[10px]">Tên hàng <span className="text-red-400">*</span></Label>
                  <Input value={form.name} onChange={e => upd("name", e.target.value)} placeholder="Nhập tên hàng" className="bg-[#0f172a] border-[#334155] text-slate-200 mt-1 h-8 text-xs" data-testid="product-name" />
                </div>
              </div>

              {/* Row 2: Loại thực đơn + Nhóm hàng */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-400 text-[10px]">Loại thực đơn</Label>
                  <select value={form.menu_type} onChange={e => upd("menu_type", e.target.value)} className="w-full mt-1 bg-[#0f172a] border border-[#334155] rounded-md px-3 py-1.5 text-xs text-slate-200" data-testid="product-menu-type">
                    {MENU_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-slate-400 text-[10px]">Nhóm hàng</Label>
                  <select value={form.category} onChange={e => upd("category", e.target.value)} className="w-full mt-1 bg-[#0f172a] border border-[#334155] rounded-md px-3 py-1.5 text-xs text-slate-200" data-testid="product-category">
                    <option value="">-- Lựa chọn --</option>
                    {flatGroups.map(g => <option key={g.path} value={g.path}>{g.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Row 3: Vị trí + Checkboxes */}
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-1.5 text-[10px] text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={form.can_sell_direct} onChange={e => upd("can_sell_direct", e.target.checked)} className="rounded w-3 h-3" />
                  Bán trực tiếp
                </label>
                <label className="flex items-center gap-1.5 text-[10px] text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={form.earn_points} onChange={e => upd("earn_points", e.target.checked)} className="rounded w-3 h-3" />
                  Tích điểm
                </label>
                <label className="flex items-center gap-1.5 text-[10px] text-slate-300 cursor-pointer">
                  <input type="checkbox" checked={form.is_addon} onChange={e => upd("is_addon", e.target.checked)} className="rounded w-3 h-3" />
                  Là món thêm (Extra topping)
                </label>
              </div>

              {/* Giá hàng hóa */}
              <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 space-y-2">
                <p className="text-[10px] text-slate-400 uppercase font-bold">Giá hàng hóa</p>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <Label className="text-slate-500 text-[9px]">Giá vốn</Label>
                    <Input type="number" value={form.cost_price} onChange={e => upd("cost_price", +e.target.value)} className="bg-[#1e293b] border-[#334155] text-slate-200 mt-0.5 h-7 text-[10px]" data-testid="product-cost" />
                  </div>
                  <div>
                    <Label className="text-slate-500 text-[9px]">Giá trước thuế</Label>
                    <Input type="number" value={form.sell_price} onChange={e => upd("sell_price", +e.target.value)} className="bg-[#1e293b] border-[#334155] text-slate-200 mt-0.5 h-7 text-[10px]" data-testid="product-price" />
                  </div>
                  <div>
                    <Label className="text-slate-500 text-[9px]">Thuế bán hàng</Label>
                    <select value={form.tax} onChange={e => upd("tax", e.target.value)} className="w-full mt-0.5 bg-[#1e293b] border border-[#334155] rounded-md px-2 py-1 text-[10px] text-slate-200">
                      <option value="0%">0%</option><option value="5%">5%</option><option value="8%">8%</option><option value="10%">10%</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-500 text-[9px]">Giá sau thuế</Label>
                    <div className="mt-0.5 h-7 bg-[#1e293b] border border-[#334155] rounded-md px-2 flex items-center text-[10px] text-amber-400 font-bold">{formatVND(priceAfterTax)}</div>
                  </div>
                </div>
              </div>

              {/* Quản lý tồn kho */}
              <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-slate-400 uppercase font-bold">Quản lý tồn kho</p>
                  <Switch checked={form.manage_stock} onCheckedChange={v => upd("manage_stock", v)} />
                </div>
                {form.manage_stock && (
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <Label className="text-slate-500 text-[9px]">Tồn kho</Label>
                      <Input type="number" value={form.stock} onChange={e => upd("stock", +e.target.value)} className="bg-[#1e293b] border-[#334155] text-slate-200 mt-0.5 h-7 text-[10px]" data-testid="product-stock" />
                    </div>
                    <div>
                      <Label className="text-slate-500 text-[9px]">Ít nhất</Label>
                      <Input type="number" value={form.min_stock} onChange={e => upd("min_stock", +e.target.value)} className="bg-[#1e293b] border-[#334155] text-slate-200 mt-0.5 h-7 text-[10px]" />
                    </div>
                    <div>
                      <Label className="text-slate-500 text-[9px]">Nhiều nhất</Label>
                      <Input type="number" value={form.max_stock} onChange={e => upd("max_stock", +e.target.value)} className="bg-[#1e293b] border-[#334155] text-slate-200 mt-0.5 h-7 text-[10px]" />
                    </div>
                  </div>
                )}
              </div>

              {/* Thuộc tính (Size, Hương vị) */}
              <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 space-y-2">
                <p className="text-[10px] text-slate-400 uppercase font-bold">Thuộc tính (Màu sắc, Kích thước)</p>
                {form.attributes.map((attr, ai) => (
                  <div key={ai} className="bg-[#1e293b] border border-[#334155] rounded-md p-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-indigo-400 font-bold">{attr.name}</span>
                      <button onClick={() => removeAttr(ai)} className="text-red-400 hover:text-red-300"><X className="w-3 h-3" /></button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {attr.values.map((v, vi) => (
                        <Badge key={vi} className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 text-[9px] gap-1">
                          {v} <button onClick={() => removeAttrVal(ai, vi)} className="hover:text-red-400"><X className="w-2 h-2" /></button>
                        </Badge>
                      ))}
                      <div className="flex gap-1">
                        <Input value={attrValue} onChange={e => setAttrValue(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { addAttrValue(ai); e.preventDefault(); } }} placeholder="Giá trị" className="bg-[#0f172a] border-[#334155] text-slate-200 h-5 w-20 text-[9px]" />
                      </div>
                    </div>
                  </div>
                ))}
                <div className="flex gap-2">
                  <select value={attrName} onChange={e => setAttrName(e.target.value)} className="bg-[#1e293b] border border-[#334155] rounded-md px-2 py-1 text-[10px] text-slate-200">
                    <option value="">Chọn thuộc tính...</option>
                    <option value="SIZE">SIZE</option>
                    <option value="HƯƠNG VỊ">HƯƠNG VỊ</option>
                    <option value="MÀU SẮC">MÀU SẮC</option>
                    <option value="TOPPING">TOPPING</option>
                  </select>
                  <Button onClick={addAttribute} size="sm" variant="outline" className="h-6 text-[9px] border-[#334155] text-slate-400">+ Thêm thuộc tính</Button>
                </div>
              </div>

              {/* Đơn vị tính */}
              <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 space-y-2">
                <p className="text-[10px] text-slate-400 uppercase font-bold">Đơn vị tính</p>
                <div>
                  <Label className="text-slate-500 text-[9px]">Đơn vị cơ bản</Label>
                  <Input value={form.unit} onChange={e => upd("unit", e.target.value)} placeholder="VD: Ly, Phần, Cái..." className="bg-[#1e293b] border-[#334155] text-slate-200 mt-0.5 h-7 text-[10px] w-32" data-testid="product-unit" />
                </div>
              </div>

              {/* Sync KiotViet */}
              <label className="flex items-center gap-2 text-[10px] text-slate-300 cursor-pointer bg-[#0f172a] border border-[#334155] rounded-lg p-3">
                <input type="checkbox" checked={form.sync_kiotviet} onChange={e => upd("sync_kiotviet", e.target.checked)} className="rounded w-3.5 h-3.5" />
                <span>Đồng bộ lên KiotViet khi lưu</span>
              </label>
            </>
          )}

          {tab === "detail" && (
            <div className="space-y-2">
              <Label className="text-slate-400 text-[10px]">Mô tả chi tiết</Label>
              <textarea rows={4} className="w-full bg-[#0f172a] border border-[#334155] rounded-md px-3 py-2 text-xs text-slate-200 resize-none" placeholder="Mô tả sản phẩm..." />
              <Label className="text-slate-400 text-[10px]">Ghi chú đặt hàng</Label>
              <Input className="bg-[#0f172a] border-[#334155] text-slate-200 h-8 text-xs" placeholder="Ghi chú nội bộ..." />
            </div>
          )}

          {tab === "ingredients" && (
            <div className="text-[10px] text-slate-500">
              <p>Thêm thành phần (NVL) để quản lý công thức chế biến. Khi bán hàng, hệ thống sẽ tự trừ kho NVL.</p>
              <p className="mt-2 text-slate-400">Tính năng này sẽ khả dụng sau khi lưu sản phẩm.</p>
            </div>
          )}

          {tab === "addons" && (
            <div className="text-[10px] text-slate-500">
              <p>Thiết lập danh sách món thêm (topping) cho sản phẩm này.</p>
              <p className="mt-2 text-slate-400">Tính năng này sẽ khả dụng sau khi lưu sản phẩm.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-[#334155] bg-[#0f172a]/50">
          <div className="flex items-center gap-2">
            <Badge className="bg-emerald-500/20 text-emerald-400 border-0 text-[9px]">Trạng thái kinh doanh</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => handleSave(false)} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-white h-8 text-xs gap-1" data-testid="save-product-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Lưu
            </Button>
            <Button onClick={() => handleSave(true)} disabled={saving} className="bg-indigo-500 hover:bg-indigo-400 text-white h-8 text-xs gap-1">
              Lưu & Thêm mới
            </Button>
            <Button variant="ghost" onClick={onClose} className="text-slate-400 h-8 text-xs">Bỏ qua</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

